import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { claimReferral, dismissReferralPrompt, getReferralMe } from '../../services/referral.api';
import { clearStoredReferral, getStoredReferral } from '../../utils/referralCapture';

const PROMPT_SESSION_KEY = 'ai.referralPromptShown';

/**
 * Runs once per signed-in session: claims an invite code captured from `?ref=`, otherwise offers
 * the one-time "Do you have a referral code?" box while a referral campaign is running.
 */
export const ReferralBootstrap: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, isAnonymous } = useAuth();
  const ranFor = useRef<string | null>(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || isAnonymous || !user?.publicId) return;
    if (ranFor.current === user.publicId) return;
    ranFor.current = user.publicId;

    void (async () => {
      const stored = getStoredReferral();
      if (stored) {
        await claimReferral(stored);
        clearStoredReferral();
        return;
      }
      try {
        if (sessionStorage.getItem(PROMPT_SESSION_KEY)) return;
      } catch {
        // Session storage unavailable: fall through, the server still guards repeats.
      }
      const me = await getReferralMe();
      if (
        me.success &&
        me.data &&
        me.data.hasActiveCampaign &&
        !me.data.promptDismissed &&
        !me.data.alreadyReferred
      ) {
        setPromptOpen(true);
      }
    })();
  }, [isAuthenticated, isAnonymous, user?.publicId]);

  const markShown = () => {
    try {
      sessionStorage.setItem(PROMPT_SESSION_KEY, '1');
    } catch {
      // Not critical.
    }
  };

  const apply = async () => {
    setBusy(true);
    setFailed(false);
    const res = await claimReferral(code.trim());
    setBusy(false);
    if (res.success && res.data?.claimed) {
      markShown();
      setPromptOpen(false);
    } else {
      setFailed(true);
    }
  };

  const skip = async () => {
    markShown();
    setPromptOpen(false);
    await dismissReferralPrompt();
  };

  return (
    <Dialog open={promptOpen} onClose={() => void skip()} maxWidth="xs" fullWidth>
      <DialogTitle>{t('referral.prompt.title', 'Have a referral code?')}</DialogTitle>
      <DialogContent>
        <p className="mb-3 text-sm text-gray-600 dark:text-slate-400">
          {t(
            'referral.prompt.body',
            'Enter the code a friend shared with you to receive your reward.'
          )}
        </p>
        <TextField
          fullWidth
          size="small"
          autoFocus
          value={code}
          placeholder={t('referral.prompt.placeholder', 'Referral code')}
          inputProps={{ maxLength: 16 }}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
        />
        {failed && (
          <Alert severity="warning" className="mt-3">
            {t('referral.prompt.failed', 'This code cannot be applied.')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={() => void skip()} disabled={busy}>
          {t('referral.prompt.skip', 'Skip')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void apply()}
          disabled={busy || code.trim().length < 4}
        >
          {t('referral.prompt.apply', 'Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
